/**
 * HR Result Detail page — view and annotate an employee's assessment result
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { penilaianApi } from '../api/penilaianApi'
import {
  RESULT_STATUS_LABELS,
  RESULT_STATUS_COLORS,
  type AssignmentResponses,
} from '../types/penilaian.types'

type Tab = 'hs' | 'ss' | 'akhlak'

const SS_LABELS: Record<number, string> = { 4: 'Selalu', 3: 'Sering', 2: 'Kadang-Kadang', 1: 'Tidak Pernah' }
const HS_LABELS: Record<number, string> = { 5: 'Excellent', 4: 'Good', 3: 'Fair', 2: 'Poor', 1: 'Unsatisfactory' }
const SCORE_POINTS: Record<number, number> = { 4: 100, 3: 75, 2: 60, 1: 50 }

function ScoreBadge({ score, max }: { score: number | null; max: number }) {
  if (score === null) return <span className="text-gray-400 text-xs">—</span>
  const pct = score / max
  const color =
    pct >= 0.8 ? 'bg-green-100 text-green-700' :
    pct >= 0.6 ? 'bg-yellow-100 text-yellow-700' :
    pct >= 0.4 ? 'bg-orange-100 text-orange-700' :
                 'bg-red-100 text-red-700'
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${color}`}>
      {score.toFixed(1)}
    </span>
  )
}

function ProgressBar({ value, max = 100, color = 'bg-blue-500' }: { value: number | null; max?: number; color?: string }) {
  const pct = value != null ? Math.min(100, Math.max(0, (value / max) * 100)) : 0
  return (
    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  )
}

export default function PenilaianResultDetailPage() {
  const { resultId } = useParams<{ resultId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState<Tab>('hs')
  const [komentar, setKomentar] = useState('')
  const [usulan, setUsulan] = useState('')
  const [hrScoreDraft, setHrScoreDraft] = useState<string>('')
  const [reviewComment, setReviewComment] = useState('')
  const [realisasiDraft, setRealisasiDraft] = useState<Record<string, string>>({})
  const [realisasiPctDraft, setRealisasiPctDraft] = useState<Record<string, string>>({})
  const [openAssessors, setOpenAssessors] = useState<Set<string>>(new Set())

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: result, isLoading: resultLoading } = useQuery({
    queryKey: ['penilaian-result', resultId],
    queryFn: () => penilaianApi.getResult(resultId!),
    enabled: !!resultId,
  })

  const { data: kpiAssignments } = useQuery({
    queryKey: ['employee-kpis', result?.period_id, result?.employee_id],
    queryFn: () => penilaianApi.getEmployeeKPIAssignments(result!.period_id, result!.employee_id),
    enabled: !!result?.period_id && !!result?.employee_id,
  })

  const { data: submittedData } = useQuery({
    queryKey: ['employee-submissions', result?.period_id, result?.employee_id],
    queryFn: () =>
      penilaianApi.getEmployeeSubmittedAssignments(result!.period_id, result!.employee_id),
    enabled: !!result?.period_id && !!result?.employee_id,
  })

  const { data: ssIndicators } = useQuery({
    queryKey: ['ss-indicators'],
    queryFn: () => penilaianApi.getSSIndicators(),
  })

  const { data: akhlakValues } = useQuery({
    queryKey: ['akhlak-values'],
    queryFn: () => penilaianApi.getAkhlakValues(),
  })

  const submissions = submittedData?.data ?? []

  const responseQueries = useQueries({
    queries: submissions.map((a) => ({
      queryKey: ['assignment-responses', a.id],
      queryFn: () => penilaianApi.getAssignmentResponses(a.id),
      enabled: submissions.length > 0,
    })),
  })
  const allResponses: AssignmentResponses[] = responseQueries
    .map((q) => q.data)
    .filter((d): d is AssignmentResponses => !!d)

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!result) return
    setKomentar(result.komentar_penilai ?? '')
    setUsulan(result.usulan_pengembangan ?? '')
    setHrScoreDraft(result.hr_final_score != null ? String(result.hr_final_score) : '')
  }, [result])

  useEffect(() => {
    if (!kpiAssignments) return
    const r: Record<string, string> = {}
    const rp: Record<string, string> = {}
    for (const a of kpiAssignments) {
      r[a.id] = a.realisasi ?? ''
      rp[a.id] = a.realisasi_pct != null ? String(a.realisasi_pct) : ''
    }
    setRealisasiDraft(r)
    setRealisasiPctDraft(rp)
  }, [kpiAssignments])

  // ── Mutations ─────────────────────────────────────────────────────────────

  const updateRealisasiMutation = useMutation({
    mutationFn: ({ id, realisasi, realisasi_pct }: { id: string; realisasi: string; realisasi_pct: number | null }) =>
      penilaianApi.updateRealisasi(id, { realisasi, realisasi_pct }),
    onSuccess: () => {
      toast.success('Realisasi disimpan')
      qc.invalidateQueries({ queryKey: ['employee-kpis', result?.period_id, result?.employee_id] })
      qc.invalidateQueries({ queryKey: ['penilaian-result', resultId] })
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal menyimpan realisasi'),
  })

  // Single save for all HR panel fields
  const saveHrMutation = useMutation({
    mutationFn: () => {
      const parsed = hrScoreDraft.trim() === '' ? null : parseFloat(hrScoreDraft)
      if (parsed !== null && (isNaN(parsed) || parsed < 0 || parsed > 100)) {
        throw new Error('Nilai akhir HR harus antara 0 dan 100')
      }
      return penilaianApi.updateNarrative(resultId!, {
        komentar_penilai: komentar || null,
        usulan_pengembangan: usulan || null,
        hr_final_score: parsed,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penilaian-result', resultId] })
      qc.invalidateQueries({ queryKey: ['penilaian-results'] })
      toast.success('Data HR disimpan')
    },
    onError: (e: any) => toast.error(e?.message || e?.error?.message || 'Gagal menyimpan'),
  })

  const reviewMutation = useMutation({
    mutationFn: async ({ action, comment }: { action: 'approve' | 'reject'; comment: string }) => {
      const parsed = hrScoreDraft.trim() === '' ? null : parseFloat(hrScoreDraft)
      if (parsed !== null && (isNaN(parsed) || parsed < 0 || parsed > 100)) {
        throw new Error('Nilai akhir HR harus antara 0 dan 100')
      }
      await penilaianApi.updateNarrative(resultId!, {
        komentar_penilai: komentar || null,
        usulan_pengembangan: usulan || null,
        hr_final_score: parsed,
      })
      return penilaianApi.hrReviewResult(resultId!, { action, comment: comment || null })
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['penilaian-result', resultId] })
      qc.invalidateQueries({ queryKey: ['penilaian-results'] })
      toast.success(vars.action === 'approve' ? 'Penilaian disetujui final' : 'Penilaian ditolak')
      setReviewComment('')
    },
    onError: (e: any) => toast.error(e?.message || e?.error?.message || 'Gagal'),
  })

  // ── Helpers ───────────────────────────────────────────────────────────────

  const avgScoreFor = (
    responses: AssignmentResponses[],
    key: 'hs_responses' | 'ss_responses' | 'akhlak_responses',
    idField: string,
    id: string
  ): number | null => {
    const scores = responses.flatMap((r) =>
      (r[key] as any[])
        .filter((item) => item[idField] === id)
        .map((item) => item.score as number)
    )
    return scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null
  }

  const toggleAssessor = (id: string) => {
    setOpenAssessors(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const ROLE_ORDER: Record<string, number> = { ATASAN: 0, BAWAHAN: 1, PEER: 2, KOMITE: 3 }
  const sortedSubmissions = submissions
    .map((sub, idx) => ({ sub, idx }))
    .sort((a, b) => (ROLE_ORDER[a.sub.assessor_role] ?? 9) - (ROLE_ORDER[b.sub.assessor_role] ?? 9))

  const computeSSScore = (responses: AssignmentResponses | undefined): number | null => {
    if (!responses || !ssIndicators?.length) return null
    let totalWeighted = 0, totalBobot = 0
    for (const ind of ssIndicators) {
      const qIds = ind.questions.map((q: any) => q.id)
      const pts = responses.ss_responses
        .filter((r: any) => qIds.includes(r.ss_question_id))
        .map((r: any) => SCORE_POINTS[r.score as number] ?? 0)
      if (!pts.length) continue
      totalWeighted += (ind.bobot as number) * (pts.reduce((a, b) => a + b, 0) / pts.length)
      totalBobot += ind.bobot as number
    }
    if (totalBobot === 0) return null
    return Math.round(totalWeighted / totalBobot * 10) / 10
  }

  const computeAKHLAKScore = (responses: AssignmentResponses | undefined): number | null => {
    if (!responses || !akhlakValues?.length) return null
    const bobot = 1 / akhlakValues.length
    let totalWeighted = 0, totalBobot = 0
    for (const val of akhlakValues) {
      const bIds = val.behaviors.map((b: any) => b.id)
      const pts = responses.akhlak_responses
        .filter((r: any) => bIds.includes(r.behavior_id))
        .map((r: any) => SCORE_POINTS[r.score as number] ?? 0)
      if (!pts.length) continue
      totalWeighted += bobot * (pts.reduce((a, b) => a + b, 0) / pts.length)
      totalBobot += bobot
    }
    if (totalBobot === 0) return null
    return Math.round(totalWeighted / totalBobot * 10) / 10
  }

  const perAssessorSs = allResponses.map(computeSSScore).filter((s): s is number => s !== null)
  const perAssessorAkhlak = allResponses.map(computeAKHLAKScore).filter((s): s is number => s !== null)
  const liveSsScore = perAssessorSs.length > 0
    ? Math.round(perAssessorSs.reduce((a, b) => a + b, 0) / perAssessorSs.length * 10) / 10
    : null
  const liveAkhlakScore = perAssessorAkhlak.length > 0
    ? Math.round(perAssessorAkhlak.reduce((a, b) => a + b, 0) / perAssessorAkhlak.length * 10) / 10
    : null
  const displaySsScore = liveSsScore ?? result?.ss_score ?? null
  const displayAkhlakScore = liveAkhlakScore ?? result?.akhlak_score ?? null
  const displayHsScore = result?.hs_score ?? null
  const displayFinalScore = displayHsScore != null
    ? Math.round(
        (
          (displayHsScore * 0.70) +
          ((displaySsScore ?? 0) * 0.20) +
          ((displayAkhlakScore ?? 0) * 0.10)
        ) * 10
      ) / 10
    : result?.final_score ?? null

  if (resultLoading) {
    return <div className="flex items-center justify-center h-64 text-gray-500">Memuat...</div>
  }
  if (!result) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Hasil penilaian tidak ditemukan.</div>
  }

  const isLocked = result.approval_status === 'approved'
  const canReview = displayFinalScore !== null &&
    result.approval_status !== 'approved' &&
    result.approval_status !== 'rejected'

  const companyKpis = (kpiAssignments ?? []).filter((k) => k.kpi?.kpi_type === 'company')
  const individualKpis = (kpiAssignments ?? []).filter((k) => k.kpi?.kpi_type === 'individual')
  const employeeName = result.employee?.display_name ?? result.employee?.email ?? '—'

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">

      {/* ── Header ── */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/penilaian/results')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Kembali
        </button>
        <span className="text-gray-200">|</span>
        <div className="flex items-center gap-3 flex-1">
          <div>
            <h1 className="text-lg font-bold text-gray-900 leading-tight">{employeeName}</h1>
            <p className="text-xs text-gray-500">{result.employee?.department ?? '—'}</p>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${RESULT_STATUS_COLORS[result.approval_status]}`}>
            {RESULT_STATUS_LABELS[result.approval_status]}
          </span>
        </div>
        {isLocked && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-lg text-xs text-gray-500">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Disetujui Final — Read Only
          </div>
        )}
      </div>

      {/* ── Two-column body ── */}
      <div className="grid grid-cols-5 gap-6 items-start">

        {/* ── LEFT: Assessment data ── */}
        <div className="col-span-3 space-y-4">

          {/* Tabs */}
          <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden">
            {([
              { key: 'hs' as Tab,     label: 'Hard Skill',  score: displayHsScore,     color: 'text-blue-600' },
              { key: 'ss' as Tab,     label: 'Soft Skill',  score: displaySsScore,     color: 'text-green-600' },
              { key: 'akhlak' as Tab, label: 'AKHLAK',      score: displayAkhlakScore, color: 'text-purple-600' },
            ]).map(({ key, label, score, color }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex flex-col items-center gap-0.5 ${
                  activeTab === key
                    ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50/50'
                    : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{label}</span>
                {score != null && (
                  <span className={`text-xs font-bold tabular-nums ${activeTab === key ? 'text-blue-600' : color}`}>
                    {score.toFixed(1)}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── Hard Skill Tab ── */}
          {activeTab === 'hs' && (
            <div className="space-y-4">
              {companyKpis.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">KPI Perusahaan</p>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">KPI</th>
                          <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide w-16">Bobot</th>
                          <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Realisasi</th>
                          <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">% Pencapaian</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {companyKpis.map((k) => (
                          <tr key={k.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900 text-sm">{k.kpi?.name}</p>
                              {k.kpi?.target_description && k.kpi.target_description !== k.kpi.name && (
                                <p className="text-xs text-gray-400 mt-0.5">{k.kpi.target_description}</p>
                              )}
                            </td>
                            <td className="px-3 py-3 text-center text-xs font-semibold text-gray-600">{k.bobot}%</td>
                            <td className="px-3 py-3 text-sm text-gray-700">
                              {k.realisasi ?? <span className="text-gray-300 italic">—</span>}
                            </td>
                            <td className="px-3 py-3 text-center">
                              {k.realisasi_pct != null ? (
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                  k.realisasi_pct >= 100 ? 'bg-green-100 text-green-700' :
                                  k.realisasi_pct >= 75  ? 'bg-yellow-100 text-yellow-700' :
                                                           'bg-red-100 text-red-700'
                                }`}>
                                  {k.realisasi_pct}%
                                </span>
                              ) : <span className="text-gray-300 text-xs">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {individualKpis.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">KPI Individu</p>
                  {individualKpis.map((k) => {
                    const questions = k.kpi?.hs_questions ?? []
                    return (
                      <div key={k.id} className="bg-white border border-gray-200 rounded-xl p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-900 text-sm">{k.kpi?.name}</h3>
                            {k.kpi?.target_description && (
                              <p className="text-xs text-gray-400 mt-0.5">{k.kpi.target_description}</p>
                            )}
                          </div>
                          <span className="text-xs font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full shrink-0">
                            {k.bobot}%
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-2 mb-3 bg-gray-50 rounded-lg p-3">
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Target</p>
                            <p className="text-sm text-gray-700">
                              {k.target_value || <span className="text-gray-300 italic">—</span>}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Realisasi</p>
                            {isLocked ? (
                              <p className="text-sm text-gray-700">{k.realisasi ?? <span className="text-gray-300 italic">—</span>}</p>
                            ) : (
                              <input
                                type="text"
                                value={realisasiDraft[k.id] ?? ''}
                                onChange={(e) => setRealisasiDraft({ ...realisasiDraft, [k.id]: e.target.value })}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                placeholder="Realisasi..."
                              />
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 mb-1">% Pencapaian</p>
                            {isLocked ? (
                              k.realisasi_pct != null
                                ? <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${k.realisasi_pct >= 100 ? 'bg-green-50 text-green-700' : k.realisasi_pct >= 75 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700'}`}>{k.realisasi_pct}%</span>
                                : <span className="text-gray-300 italic text-sm">—</span>
                            ) : (
                              <div className="flex gap-1">
                                <input
                                  type="number"
                                  value={realisasiPctDraft[k.id] ?? ''}
                                  onChange={(e) => setRealisasiPctDraft({ ...realisasiPctDraft, [k.id]: e.target.value })}
                                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                  placeholder="0"
                                  min={0} max={110}
                                />
                                <button
                                  onClick={() => updateRealisasiMutation.mutate({
                                    id: k.id,
                                    realisasi: realisasiDraft[k.id] ?? '',
                                    realisasi_pct: realisasiPctDraft[k.id] ? parseFloat(realisasiPctDraft[k.id]) : null,
                                  })}
                                  disabled={updateRealisasiMutation.isPending || !realisasiDraft[k.id]}
                                  className="px-2 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-800 disabled:opacity-40 whitespace-nowrap"
                                >
                                  OK
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {questions.length > 0 && allResponses.length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                              Penilaian ({submissions.length} penilai)
                            </p>
                            {questions.map((q, i) => {
                              const avg = avgScoreFor(allResponses, 'hs_responses', 'hs_question_id', q.id)
                              return (
                                <div key={q.id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                                  <p className="text-xs text-gray-600 flex-1 pr-4">{i + 1}. {q.question_text}</p>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <ScoreBadge score={avg} max={5} />
                                    {avg !== null && <span className="text-xs text-gray-400">{HS_LABELS[Math.round(avg)]}</span>}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}

                        {(() => {
                          const kpiNotes = responseQueries
                            .map((q, i) => ({ submission: submissions[i], note: q.data?.notes.find((n) => n.note_type === 'kpi' && n.reference_id === k.id) }))
                            .filter((x) => x.note?.note_text)
                          if (!kpiNotes.length) return null
                          return (
                            <div className="mt-3 border-t border-gray-100 pt-3 space-y-2">
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Catatan Penilai</p>
                              {kpiNotes.map(({ submission, note }) => (
                                <div key={submission.id} className="bg-amber-50 border border-amber-100 rounded-lg p-2.5">
                                  <p className="text-xs font-medium text-gray-500 mb-0.5">
                                    {submission.assessor?.display_name ?? submission.assessor?.email}
                                    <span className="ml-1 text-gray-400">({submission.assessor_role})</span>
                                  </p>
                                  <p className="text-xs text-gray-700">{note!.note_text}</p>
                                </div>
                              ))}
                            </div>
                          )
                        })()}
                      </div>
                    )
                  })}
                </div>
              )}

              {(kpiAssignments ?? []).length === 0 && (
                <p className="text-sm text-gray-400 italic text-center py-8">Tidak ada KPI untuk pegawai ini.</p>
              )}
            </div>
          )}

          {/* ── Soft Skill Tab ── */}
          {activeTab === 'ss' && (
            <div className="space-y-3">
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-gray-900 text-sm">
                    Rata-rata <span className="text-gray-400 font-normal">({submissions.length} penilai)</span>
                  </h2>
                  <span className="text-xl font-bold text-green-700">{displaySsScore?.toFixed(1) ?? '—'}</span>
                </div>
                {allResponses.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Belum ada penilaian.</p>
                ) : (
                  <div className="space-y-3">
                    {(ssIndicators ?? []).map((indicator) => {
                      const qIds = indicator.questions.map((q: any) => q.id)
                      const allScores = allResponses.flatMap((r) =>
                        r.ss_responses.filter((s: any) => qIds.includes(s.ss_question_id)).map((s: any) => s.score as number)
                      )
                      const indAvg = allScores.length > 0 ? allScores.reduce((a, b) => a + b, 0) / allScores.length : null
                      return (
                        <div key={indicator.id}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-800 text-sm">{indicator.name}</span>
                              <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                                {(indicator.bobot as number * 100).toFixed(0)}%
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <ScoreBadge score={indAvg} max={4} />
                              {indAvg !== null && <span className="text-xs text-gray-400">{SS_LABELS[Math.round(indAvg)]}</span>}
                            </div>
                          </div>
                          <div className="pl-3 border-l-2 border-gray-100 space-y-0.5">
                            {indicator.questions.map((q: any, i: number) => {
                              const avg = avgScoreFor(allResponses, 'ss_responses', 'ss_question_id', q.id)
                              return (
                                <div key={q.id} className="flex items-center justify-between py-0.5">
                                  <p className="text-xs text-gray-600 flex-1 pr-4">{i + 1}. {q.question_text}</p>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <ScoreBadge score={avg} max={4} />
                                    {avg !== null && <span className="text-xs text-gray-400">{SS_LABELS[Math.round(avg)]}</span>}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {sortedSubmissions.map(({ sub, idx }, displayIdx) => {
                const responses = responseQueries[idx]?.data
                const isOpen = openAssessors.has(sub.id)
                const ssScore = computeSSScore(responses)
                return (
                  <div key={sub.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleAssessor(sub.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900 text-sm">Penilai {displayIdx + 1}</span>
                      <div className="flex items-center gap-3">
                        {ssScore !== null && <span className="text-sm font-bold text-green-700">{ssScore.toFixed(1)}</span>}
                        <span className="text-gray-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 pt-3 border-t border-gray-100 space-y-3">
                        {(ssIndicators ?? []).map((indicator) => {
                          const qIds = indicator.questions.map((q: any) => q.id)
                          const indScores = (responses?.ss_responses ?? []).filter((r: any) => qIds.includes(r.ss_question_id)).map((r: any) => r.score as number)
                          const indAvg = indScores.length > 0 ? indScores.reduce((a, b) => a + b, 0) / indScores.length : null
                          return (
                            <div key={indicator.id}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-800 text-sm">{indicator.name}</span>
                                  <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{(indicator.bobot as number * 100).toFixed(0)}%</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <ScoreBadge score={indAvg} max={4} />
                                  {indAvg !== null && <span className="text-xs text-gray-400">{SS_LABELS[Math.round(indAvg)]}</span>}
                                </div>
                              </div>
                              <div className="pl-3 border-l-2 border-gray-100 space-y-0.5">
                                {indicator.questions.map((q: any, i: number) => {
                                  const resp = (responses?.ss_responses ?? []).find((r: any) => r.ss_question_id === q.id)
                                  return (
                                    <div key={q.id} className="flex items-center justify-between py-0.5">
                                      <p className="text-xs text-gray-600 flex-1 pr-4">{i + 1}. {q.question_text}</p>
                                      <div className="flex items-center gap-1 shrink-0">
                                        <ScoreBadge score={resp?.score ?? null} max={4} />
                                        {resp && <span className="text-xs text-gray-400">{SS_LABELS[resp.score]}</span>}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* ── AKHLAK Tab ── */}
          {activeTab === 'akhlak' && (
            <div className="space-y-3">
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-gray-900 text-sm">
                    Rata-rata <span className="text-gray-400 font-normal">({submissions.length} penilai)</span>
                  </h2>
                  <span className="text-xl font-bold text-purple-700">{displayAkhlakScore?.toFixed(1) ?? '—'}</span>
                </div>
                {allResponses.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Belum ada penilaian.</p>
                ) : (
                  <div className="space-y-3">
                    {(akhlakValues ?? []).map((value) => {
                      const bIds = value.behaviors.map((b: any) => b.id)
                      const allScores = allResponses.flatMap((r) =>
                        r.akhlak_responses.filter((s: any) => bIds.includes(s.behavior_id)).map((s: any) => s.score as number)
                      )
                      const valAvg = allScores.length > 0 ? allScores.reduce((a, b) => a + b, 0) / allScores.length : null
                      return (
                        <div key={value.id}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-800 text-sm">{value.name}</span>
                            <div className="flex items-center gap-1">
                              <ScoreBadge score={valAvg} max={4} />
                              {valAvg !== null && <span className="text-xs text-gray-400">{SS_LABELS[Math.round(valAvg)]}</span>}
                            </div>
                          </div>
                          <div className="pl-3 border-l-2 border-gray-100 space-y-0.5">
                            {value.behaviors.map((b: any, i: number) => {
                              const avg = avgScoreFor(allResponses, 'akhlak_responses', 'behavior_id', b.id)
                              return (
                                <div key={b.id} className="flex items-center justify-between py-0.5">
                                  <p className="text-xs text-gray-600 flex-1 pr-4">{i + 1}. {b.behavior_text}</p>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <ScoreBadge score={avg} max={4} />
                                    {avg !== null && <span className="text-xs text-gray-400">{SS_LABELS[Math.round(avg)]}</span>}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {sortedSubmissions.map(({ sub, idx }, displayIdx) => {
                const responses = responseQueries[idx]?.data
                const isOpen = openAssessors.has(sub.id)
                const akhlakScore = computeAKHLAKScore(responses)
                return (
                  <div key={sub.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleAssessor(sub.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900 text-sm">Penilai {displayIdx + 1}</span>
                      <div className="flex items-center gap-3">
                        {akhlakScore !== null && <span className="text-sm font-bold text-purple-700">{akhlakScore.toFixed(1)}</span>}
                        <span className="text-gray-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 pt-3 border-t border-gray-100 space-y-3">
                        {(akhlakValues ?? []).map((value) => {
                          const bIds = value.behaviors.map((b: any) => b.id)
                          const valScores = (responses?.akhlak_responses ?? []).filter((r: any) => bIds.includes(r.behavior_id)).map((r: any) => r.score as number)
                          const valAvg = valScores.length > 0 ? valScores.reduce((a, b) => a + b, 0) / valScores.length : null
                          return (
                            <div key={value.id}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-gray-800 text-sm">{value.name}</span>
                                <div className="flex items-center gap-1">
                                  <ScoreBadge score={valAvg} max={4} />
                                  {valAvg !== null && <span className="text-xs text-gray-400">{SS_LABELS[Math.round(valAvg)]}</span>}
                                </div>
                              </div>
                              <div className="pl-3 border-l-2 border-gray-100 space-y-0.5">
                                {value.behaviors.map((b: any, i: number) => {
                                  const resp = (responses?.akhlak_responses ?? []).find((r: any) => r.behavior_id === b.id)
                                  return (
                                    <div key={b.id} className="py-0.5">
                                      <div className="flex items-center justify-between">
                                        <p className="text-xs text-gray-600 flex-1 pr-4">{i + 1}. {b.behavior_text}</p>
                                        <div className="flex items-center gap-1 shrink-0">
                                          <ScoreBadge score={resp?.score ?? null} max={4} />
                                          {resp && <span className="text-xs text-gray-400">{SS_LABELS[resp.score]}</span>}
                                        </div>
                                      </div>
                                      {resp?.tindakan_aktual && (
                                        <p className="text-xs text-gray-400 italic mt-0.5 pl-3">{resp.tindakan_aktual}</p>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Approval history */}
          {result.approvals.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h2 className="font-semibold text-gray-900 text-sm mb-3">Riwayat Approval</h2>
              <div className="space-y-2.5">
                {result.approvals.map((a) => (
                  <div key={a.id} className="flex items-start gap-3 pb-2.5 border-b border-gray-100 last:border-0 last:pb-0">
                    <div className={`shrink-0 w-2 h-2 rounded-full mt-1.5 ${a.status === 'approved' ? 'bg-green-500' : a.status === 'rejected' ? 'bg-red-500' : 'bg-gray-300'}`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">{a.approver?.display_name ?? a.approver?.email ?? '—'}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'approved' ? 'bg-green-100 text-green-700' : a.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                          {a.status === 'approved' ? 'Disetujui' : a.status === 'rejected' ? 'Ditolak' : 'Menunggu'}
                        </span>
                      </div>
                      {a.comment && <p className="text-xs text-gray-500 mt-0.5">{a.comment}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT: HR Action Panel (sticky) ── */}
        <div className="col-span-2">
          <div className="sticky top-4 space-y-4">

            {/* Score summary */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Ringkasan Nilai</p>
              <div className="space-y-2.5">
                {[
                  { label: 'Hard Skill', weight: '70%', score: displayHsScore, bar: 'bg-blue-500' },
                  { label: 'Soft Skill', weight: '20%', score: displaySsScore, bar: 'bg-green-500' },
                  { label: 'AKHLAK',     weight: '10%', score: displayAkhlakScore, bar: 'bg-purple-500' },
                ].map(({ label, weight, score, bar }) => (
                  <div key={label} className="flex items-center gap-2">
                    <div className="w-24 shrink-0">
                      <span className="text-xs text-gray-700 font-medium">{label}</span>
                      <span className="text-xs text-gray-400 ml-1">{weight}</span>
                    </div>
                    <ProgressBar value={score} color={bar} />
                    <span className="w-10 text-right text-sm font-bold tabular-nums text-gray-700">
                      {score != null ? score.toFixed(1) : '—'}
                    </span>
                  </div>
                ))}
                <div className="border-t border-gray-100 pt-2.5 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">Nilai Akhir</span>
                  <span className="text-2xl font-extrabold text-gray-900 tabular-nums">
                    {displayFinalScore != null ? displayFinalScore.toFixed(1) : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* HR inputs — only shown when not yet approved */}
            {!isLocked && (
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Input HR</p>

                {result.komentar_pekerja && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Komentar Pekerja</p>
                    <p className="text-xs text-gray-700">{result.komentar_pekerja}</p>
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Komentar Penilai</label>
                    <textarea
                      value={komentar}
                      onChange={(e) => setKomentar(e.target.value)}
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      placeholder="Tambahkan komentar penilai..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Usulan Pengembangan</label>
                    <textarea
                      value={usulan}
                      onChange={(e) => setUsulan(e.target.value)}
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      placeholder="Usulan pengembangan untuk pegawai..."
                    />
                  </div>
                  <button
                    onClick={() => saveHrMutation.mutate()}
                    disabled={saveHrMutation.isPending}
                    className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors"
                  >
                    {saveHrMutation.isPending ? 'Menyimpan...' : 'Simpan'}
                  </button>
                </div>
              </div>
            )}

            {/* Approve / Reject */}
            {canReview && (
              <div className="bg-white border border-yellow-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Keputusan Final</p>

                {/* Nilai Final HR */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Nilai Final HR
                    <span className="ml-1 text-gray-400 font-normal">(0–100, opsional)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0} max={100} step={0.1}
                      value={hrScoreDraft}
                      onChange={(e) => setHrScoreDraft(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="mis. 82.5"
                    />
                    {hrScoreDraft && (
                      <button
                        type="button"
                        onClick={() => setHrScoreDraft('')}
                        className="text-xs text-gray-400 hover:text-red-500 px-1"
                        title="Hapus nilai"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {displayFinalScore != null && (
                    <p className="text-xs text-gray-400 mt-1">
                      Komputasi otomatis: <span className="font-semibold">{displayFinalScore.toFixed(1)}</span>
                    </p>
                  )}
                </div>

                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 resize-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                  placeholder="Komentar keputusan (opsional)..."
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => reviewMutation.mutate({ action: 'reject', comment: reviewComment })}
                    disabled={reviewMutation.isPending}
                    className="py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 text-sm font-medium transition-colors"
                  >
                    Tolak
                  </button>
                  <button
                    onClick={() => reviewMutation.mutate({ action: 'approve', comment: reviewComment })}
                    disabled={reviewMutation.isPending}
                    className="py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                  >
                    {reviewMutation.isPending ? 'Memproses...' : 'Setujui Final'}
                  </button>
                </div>
              </div>
            )}

            {/* Approved — prominent result card */}
            {isLocked && (() => {
              const approval = result.approvals.find((a) => a.status === 'approved')
              const heroScore = result.hr_final_score ?? displayFinalScore
              return (
                <div className="bg-white border border-green-300 rounded-xl overflow-hidden">
                  {/* Green header */}
                  <div className="bg-green-600 px-4 py-3 flex items-center gap-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-semibold text-white">Disetujui Final</span>
                    {approval?.approved_at && (
                      <span className="ml-auto text-xs text-green-200">
                        {new Date(approval.approved_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>

                  <div className="p-4 space-y-4">
                    {/* Hero score */}
                    {heroScore != null && (
                      <div className="flex items-center gap-4 p-3 bg-green-50 rounded-xl border border-green-100">
                        <div className="text-center">
                          <p className="text-4xl font-extrabold text-green-700 tabular-nums leading-none">
                            {heroScore.toFixed(1)}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">/ 100</p>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-green-600 uppercase tracking-wide">
                            {result.hr_final_score != null ? 'Nilai Resmi HR' : 'Nilai Akhir'}
                          </p>
                          {result.hr_final_score != null && displayFinalScore != null && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              Nilai komputasi: <span className="font-semibold text-gray-600">{displayFinalScore.toFixed(1)}</span>
                            </p>
                          )}
                          {approval && (
                            <p className="text-xs text-gray-500 mt-1">
                              Oleh <span className="font-medium text-gray-700">{approval.approver?.display_name ?? approval.approver?.email ?? '—'}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Approval comment */}
                    {approval?.comment && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Komentar Keputusan</p>
                        <p className="text-sm text-gray-700 italic">"{approval.comment}"</p>
                      </div>
                    )}

                    {/* HR narrative */}
                    {(result.komentar_penilai || result.usulan_pengembangan) && (
                      <div className="space-y-3 pt-3 border-t border-gray-100">
                        {result.komentar_penilai && (
                          <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Komentar Penilai</p>
                            <p className="text-sm text-gray-700">{result.komentar_penilai}</p>
                          </div>
                        )}
                        {result.usulan_pengembangan && (
                          <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Usulan Pengembangan</p>
                            <p className="text-sm text-gray-700">{result.usulan_pengembangan}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Employee comment */}
                    {result.komentar_pekerja && (
                      <div className="pt-3 border-t border-gray-100">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Komentar Pekerja</p>
                        <p className="text-sm text-gray-600 italic">"{result.komentar_pekerja}"</p>
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}

          </div>
        </div>

      </div>
    </div>
  )
}
